import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { Formik, Form } from 'formik';
import lodashPick from 'lodash/fp/pick';
import lodashOmit from 'lodash/fp/omit';
import lodashTimes from 'lodash/fp/times';
import lodashCompact from 'lodash/fp/compact';
import pluralize from 'pluralize';

import {
  PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
  PROPERTY_ATTRIBUTE_CASCADING_BEHAVIOR_DEFAULT,
} from 'common/propertyAttributeConstants';

import {
  TENANT_SCREENING_CRITERIA_MOVE_IN_MINIMUM_DAYS_DEFAULT,
  TENANT_SCREENING_CRITERIA_MOVE_IN_DATE_RANGE_IN_DAYS_DEFAULT,
} from 'common/tenantScreeningCriteriaConstants';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import { USER_ROLE_ADMIN, USER_ROLE_PARTNER } from 'common/userRoles';

import getListingFormFields from 'common/listingFormFields';
import getTenantScreeningCriteriaFormFields from 'common/tenantScreeningCriteriaFormFields';
import { cleanMarketingPartnerListingFormValues } from 'common/marketingPartnerListingHelpers';

import {
  SR_API_ACTION_TENANT_SCREENING_CRITERIA_UPSERT,
  SR_API_ACTION_PROPERTY_ATTRIBUTE_UPSERT_LIST,
  SR_API_ACTION_UPDATE_BREED_RESTRICTIONS,
  SR_API_ACTION_PARTNER_UPDATE_PET_SCREENING,
} from 'api/actionTypes';

import { SCREEN_ID_PARTNER_SETTINGS_APPLICATIONS } from 'client/constants/screenIds';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles, { getUserLandingPage } from 'client/helpers/allowedRoles';
import {
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import branch from 'client/core/branch';
import PartnerActions from 'client/actions/Partner';
import PropertyAttributeActions from 'client/actions/PropertyAttribute';
import TenantScreeningCriteriaActions from 'client/actions/TenantScreeningCriteria';
import postWithState from 'client/actions/postWithState';
import { getPropertyAttributeListKeyId } from 'client/actions/PropertyAttribute/getList';

import SrText from 'client/components/SrText';
import SrCard, {
  SR_CARD_BULLET_COLOR_PURPLE,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
  SR_CARD_CONTENT_TYPE_FORM_REGULAR,
} from 'client/components/SrCard/SrCard';
import SrBox from 'client/components/SrBox';
import { SR_CHECKBOX_THEME_BOX } from 'client/components/Checkbox';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
  SR_FIELD_THEME_ADOBE_LONG_LABEL,
} from 'client/components/SrFormikField';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrRouteLeavingGuard from 'client/components/SrRouteLeavingGuard';
import SrFooterButton from 'client/components/SrFooterButton/SrFooterButton';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrBreedRestrictionsModal from 'client/components/SrBreedRestrictionsModal';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';

const FormikContext = React.createContext();
const tenantScreeningCriteriaFieldgroup = [
  'creditScore',
  'creditScoreQualification',
  'rentToIncomeRatio',
  'moveInDateRangeInDays',
  'feloniesAccepted',
  'feloniesExpirationInYears',
  'bankruptciesAccepted',
  'bankruptciesExpirationInYears',
  'evictionsAccepted',
  'evictionsExpirationInYears',
  'section8Accepted',
  'guarantorAccepted',
  'guarantorRentToIncomeRatio',
  'verifyRentalHistory',
  'verifyRentalHistoryOnMinCreditScore',
];
const listingPropertyAttributes = [
  'maximumPetsAllowed',
  'petFee',
  'petDeposit',
  'monthlyPetRent',
  'catsAllowed',
  'dogsAllowed',
  'petWeight',
  'petDeposit',
  'petDepositPerPet',
  'additionalPetDeposit',
  'petFee',
  'petFeePerPet',
  'additionalPetFee',
  'monthlyPetRent',
  'monthlyPetRentPerPet',
  'additionalMonthlyPetRent',
];

const pickTenantScreeningCriteriaFields = lodashPick(tenantScreeningCriteriaFieldgroup);
const pickListingFields = lodashPick(listingPropertyAttributes);

const omitTenantScreeningCriteriaFields = lodashOmit([
  'id',
  'breedRestrictions',
  'feloniesExpirationInYears',
  'bankruptciesExpirationInYears',
  'evictionsExpirationInYears',
]);

const petScreeningFieldIds = [
  'petScreeningUrl',
  'petScreeningLoginUserName',
  'petScreeningLoginPassword',
  'petScreeningLoginPasswordConfirm',
];

const getFormFields = ({ formValues, setFieldValue }) => {
  const missingPetScreeningFields = petScreeningFieldIds.filter((id) => {
    return !formValues[id];
  });
  const petScreeningFieldsRequired =
    missingPetScreeningFields.length !== petScreeningFieldIds.length;

  const shouldHidePetsField = !formValues.catsAllowed && !formValues.dogsAllowed;
  const listingFormFields = getListingFormFields({
    fieldgroup: listingPropertyAttributes,
    required: true,
    updates: {
      catsAllowed: {
        type: 'yes_no',
      },
      dogsAllowed: {
        type: 'yes_no',
      },
      petWeight: {
        title: 'Max Dog Weight',
        hideFieldIf: formValues.dogsAllowed !== true,
      },
      maximumPetsAllowed: {
        hideFieldIf: shouldHidePetsField,
      },
      petDeposit: {
        hideFieldIf: shouldHidePetsField,
      },
      petDepositPerPet: {
        title: 'How is the deposit structured?',
        type: 'multiple_choice',
        validations: ['required'],
        hideFieldIf: shouldHidePetsField,
        properties: {
          choices: [
            {
              label: 'Per Pet',
              value: true,
            },
            {
              label: 'Covers All Pets',
              value: false,
            },
          ],
          multipleSelection: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '265px',
        },
      },
      additionalPetDeposit: {
        title: "What's the deposit per additional pet?",
        hideFieldIf: shouldHidePetsField || !formValues.petDepositPerPet,
      },
      petFee: {
        hideFieldIf: shouldHidePetsField,
      },
      petFeePerPet: {
        title: 'How is the fee structured?',
        type: 'multiple_choice',
        validations: ['required'],
        hideFieldIf: shouldHidePetsField,
        properties: {
          choices: [
            {
              label: 'Per Pet',
              value: true,
            },
            {
              label: 'Covers All Pets',
              value: false,
            },
          ],
          multipleSelection: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '265px',
        },
      },
      additionalPetFee: {
        title: "What's the fee per additional pet?",
        hideFieldIf: shouldHidePetsField || !formValues.petFeePerPet,
      },
      monthlyPetRent: {
        hideFieldIf: shouldHidePetsField,
      },
      monthlyPetRentPerPet: {
        title: 'How is the monthly rent structured?',
        type: 'multiple_choice',
        validations: ['required'],
        hideFieldIf: shouldHidePetsField,
        properties: {
          choices: [
            {
              label: 'Per Pet',
              value: true,
            },
            {
              label: 'Covers All Pets',
              value: false,
            },
          ],
          multipleSelection: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '265px',
        },
      },
      additionalMonthlyPetRent: {
        title: "What's the monthly rent per additional pet?",
        hideFieldIf: shouldHidePetsField || !formValues.monthlyPetRentPerPet,
      },
    },
  });

  const yearChoices = lodashTimes(
    (value) => ({
      value,
      label: value === 0 ? 'Any Time' : `${value}+ Years Ago`,
    }),
    8,
  );
  const tenantScreeningCriteriaFormFields = getTenantScreeningCriteriaFormFields({
    fieldgroup: tenantScreeningCriteriaFieldgroup,
    required: true,
    updates: {
      rentToIncomeRatio: {
        type: 'select',
        properties: {
          transformToFloat: true,
          choices: lodashTimes((index) => {
            const value = index * 0.25 + 2;
            return {
              value,
              label: `${value}x Rent` + (value === 3 ? ' (recommended)' : ''),
            };
          }, 19),
        },
      },
      feloniesExpirationInYears: {
        type: 'select',
        properties: {
          transformToNumber: true,
          choices: yearChoices,
        },
        hideFieldIf: !formValues.feloniesAccepted,
      },
      bankruptciesExpirationInYears: {
        type: 'select',
        properties: {
          transformToNumber: true,
          choices: yearChoices,
        },
        hideFieldIf: !formValues.bankruptciesAccepted,
      },
      evictionsExpirationInYears: {
        type: 'select',
        properties: {
          transformToNumber: true,
          choices: yearChoices,
        },
        hideFieldIf: !formValues.evictionsAccepted,
      },
      guarantorRentToIncomeRatio: {
        type: 'select',
        properties: {
          transformToFloat: true,
          choices: lodashTimes((index) => {
            const value = index * 0.25 + 3;
            return {
              value,
              label: `${value}x Rent` + (value === 3 ? ' (recommended)' : ''),
            };
          }, 15),
        },
        hideFieldIf: !formValues.guarantorAccepted,
      },
      verifyRentalHistory: {
        properties: {
          onChange: (verifyRentalHistory) => {
            if (!verifyRentalHistory && setFieldValue) {
              setFieldValue('verifyRentalHistoryOnMinCreditScore', '');
            }
          },
        },
      },
      verifyRentalHistoryOnMinCreditScore: {
        hideFieldIf: !formValues.verifyRentalHistory,
        properties: {
          noRequired: true,
        },
      },
    },
  });

  const petScreeningFields = [
    {
      id: 'petScreeningUrl',
      title: 'PetScreening.com URL for Applicants',
      type: 'short_text',
      validations: lodashCompact(['url', petScreeningFieldsRequired && 'required']),
    },
    {
      id: 'petScreeningLoginUserName',
      title: 'Email',
      type: 'email',
      validations: lodashCompact(['email', petScreeningFieldsRequired && 'required']),
    },
    {
      id: 'petScreeningLoginPassword',
      title: 'Password',
      type: 'short_text',
      validations: lodashCompact([petScreeningFieldsRequired && 'required']),
    },
    {
      id: 'petScreeningLoginPasswordConfirm',
      title: 'Confirm Password',
      type: 'short_text',
      validations: lodashCompact([
        'confirmPassword:petScreeningLoginPassword' +
          '::Sorry these passwords do not match. Please type them in again.',
        petScreeningFieldsRequired && 'required',
      ]),
    },
  ];

  return [...tenantScreeningCriteriaFormFields, ...listingFormFields, ...petScreeningFields];
};

const getDefaultFormValues = ({
  partnerTenantScreeningCriteria,
  propertyAttributes = [],
  partner,
}) => {
  const tenantScreeningCriteria = partnerTenantScreeningCriteria || {};
  const defaultFormValues = {
    ...pickTenantScreeningCriteriaFields(tenantScreeningCriteria),
  };

  if (typeof tenantScreeningCriteria.feloniesExpirationInMonths === 'number') {
    defaultFormValues.feloniesExpirationInYears =
      tenantScreeningCriteria.feloniesExpirationInMonths / 12;
  }

  if (typeof tenantScreeningCriteria.bankruptciesExpirationInMonths === 'number') {
    defaultFormValues.bankruptciesExpirationInYears =
      tenantScreeningCriteria.bankruptciesExpirationInMonths / 12; // eslint-disable-line max-len
  }

  if (typeof tenantScreeningCriteria.evictionsExpirationInMonths === 'number') {
    defaultFormValues.evictionsExpirationInYears =
      tenantScreeningCriteria.evictionsExpirationInMonths / 12;
  }

  propertyAttributes.forEach((propertyAttribute) => {
    defaultFormValues[propertyAttribute.name] = propertyAttribute.attrs.value;
  });

  defaultFormValues.petScreeningUrl = partner.petScreeningUrl;
  defaultFormValues.petScreeningLoginUserName = partner.petScreeningLoginUserName;
  defaultFormValues.petScreeningLoginPassword = partner.petScreeningLoginPassword;
  defaultFormValues.petScreeningLoginPasswordConfirm = partner.petScreeningLoginPassword;

  return defaultFormValues;
};

const getTenantScreeningCriteriaPayload = ({
  partner,
  partnerTenantScreeningCriteria,
  tenantScreeningCriteriaUpdates,
}) => {
  const tenantScreeningCriteria = {
    ...tenantScreeningCriteriaUpdates,
  };

  if (typeof tenantScreeningCriteria.feloniesExpirationInYears === 'number') {
    tenantScreeningCriteria.feloniesExpirationInMonths =
      tenantScreeningCriteria.feloniesExpirationInYears * 12; // eslint-disable-line max-len
    tenantScreeningCriteria.feloniesDescription =
      tenantScreeningCriteria.feloniesExpirationInYears > 0
        ? `in the past ${tenantScreeningCriteria.feloniesExpirationInYears} years`
        : null;
  }

  if (typeof tenantScreeningCriteria.bankruptciesExpirationInYears === 'number') {
    tenantScreeningCriteria.bankruptciesExpirationInMonths =
      tenantScreeningCriteria.bankruptciesExpirationInYears * 12; // eslint-disable-line max-len
    tenantScreeningCriteria.bankruptciesDescription =
      tenantScreeningCriteria.bankruptciesExpirationInYears > 0 // eslint-disable-line max-len
        ? `in the past ${tenantScreeningCriteria.bankruptciesExpirationInYears} years`
        : null;
  }

  if (typeof tenantScreeningCriteria.evictionsExpirationInYears === 'number') {
    tenantScreeningCriteria.evictionsExpirationInMonths =
      tenantScreeningCriteria.evictionsExpirationInYears * 12; // eslint-disable-line max-len
    tenantScreeningCriteria.evictionsDescription =
      tenantScreeningCriteria.evictionsExpirationInYears > 0
        ? `in the past ${tenantScreeningCriteria.evictionsExpirationInYears} years`
        : null;
  }

  return {
    partnerId: partner.id,
    tenantScreeningCriteria: omitTenantScreeningCriteriaFields({
      moveInMinimumDays: TENANT_SCREENING_CRITERIA_MOVE_IN_MINIMUM_DAYS_DEFAULT,
      moveInDateRangeInDays: TENANT_SCREENING_CRITERIA_MOVE_IN_DATE_RANGE_IN_DAYS_DEFAULT,
      ...partnerTenantScreeningCriteria,
      ...tenantScreeningCriteria,
    }),
  };
};

@withTransitionHook
@allowedRoles({
  roles: [USER_ROLE_ADMIN, USER_ROLE_PARTNER],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_PARTNER_SETTINGS_APPLICATIONS)
@withScrollToTop
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  partnerTenantScreeningCriteria: [
    'tenantScreeningCriteria',
    'byPartnerSlug',
    match.params.partnerSlug,
  ],
  breedRestrictionDefinitions: ['breedRestrictions', 'definitions'],
  currentUser: ['currentUser'],
}))
@branch(({ partner }) => {
  const cursors = {};

  if (partner) {
    cursors.propertyAttributes = [
      'propertyAttributes',
      'listBy',
      getPropertyAttributeListKeyId({
        partnerId: partner.id,
        category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
      }),
    ];
  }

  return cursors;
})
class PartnerSettingsApplicationsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Settings',
    section: 'Partner',
  });

  static fetchData(match, inject) {
    const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    return Promise.all(
      lodashCompact([
        !partner &&
          PartnerActions.loadPartnerBySlug(inject, {
            slug: match.params.partnerSlug,
          }),
        TenantScreeningCriteriaActions.getTenantScreeningCriteria(inject, {
          partnerSlug: match.params.partnerSlug,
        }),
        PartnerActions.loadBreedRestrictionDefinitions(inject),
      ]),
    ).then(() => {
      const partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
      return PropertyAttributeActions.getList(inject, {
        partnerId: partner.id,
        category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
      });
    });
  }

  static propTypes = {
    partner: PropTypes.object,
    currentUser: PropTypes.object,
    partnerTenantScreeningCriteria: PropTypes.object,
    propertyAttributes: PropTypes.array,
    breedRestrictionDefinitions: PropTypes.array,
    isTransitioning: PropTypes.bool,
    dispatch: PropTypes.func,
  };

  constructor() {
    super();

    this.state = {
      isSubmitting: false,
      editSuccessModalIsOpen: false,
      showBreedRestrictionsModal: false,
    };
  }

  componentDidMount() {
    if (process.browser) {
      global.addEventListener('beforeunload', this._onBeforeUnload, true);
    }
  }

  componentWillUnmount() {
    if (process.browser) {
      global.removeEventListener('beforeunload', this._onBeforeUnload, true);
    }
  }

  _onBeforeUnload = (event) => {
    if (this.dirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  };

  _onSubmit = (_formValues, formikBag) => {
    this.setState({ isSubmitting: true });

    const formValues = { ..._formValues };

    const {
      partner,
      partnerTenantScreeningCriteria,
      propertyAttributes,
      dispatch,
      match,
    } = this.props;

    const tenantScreeningCriteriaUpdates = setNullEmptyValues(
      pickTenantScreeningCriteriaFields(formValues),
    );
    const listingUpdates = cleanMarketingPartnerListingFormValues(pickListingFields(formValues));
    const propertyAttributesByName = propertyAttributes.reduce(
      (memo, propertyAttribute) => ({
        ...memo,
        [propertyAttribute.name]: propertyAttribute,
      }),
      {},
    );
    const breedRestrictionsRemoved = !listingUpdates.dogsAllowed;

    const newPropertyAttributesNames = Object.keys(listingUpdates).filter((fieldId) => {
      return listingUpdates[fieldId] !== null;
    });

    const newPropertyAttributesValues = newPropertyAttributesNames.map((fieldId) => ({
      id: propertyAttributesByName[fieldId]?.id,
      category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
      cascadingBehavior: PROPERTY_ATTRIBUTE_CASCADING_BEHAVIOR_DEFAULT,
      name: fieldId,
      attrs: {
        value: listingUpdates[fieldId],
      },
      partnerId: partner.id,
    }));

    const hasPetScreeningFields = petScreeningFieldIds.every((id) => formValues[id]);

    if (!hasPetScreeningFields || (!formValues.catsAllowed && !formValues.dogsAllowed)) {
      formValues.petScreeningUrl = null;
      formValues.petScreeningLoginUserName = null;
      formValues.petScreeningLoginPassword = null;
      formValues.petScreeningLoginPasswordConfirm = null;

      for (const id of petScreeningFieldIds) {
        formikBag.setFieldValue(id, '');
      }
    }

    return Promise.all(
      lodashCompact([
        dispatch(postWithState, {
          action: SR_API_ACTION_TENANT_SCREENING_CRITERIA_UPSERT,
          payload: getTenantScreeningCriteriaPayload({
            partner,
            partnerTenantScreeningCriteria,
            tenantScreeningCriteriaUpdates,
          }),
        }),
        newPropertyAttributesValues.length &&
          dispatch(postWithState, {
            action: SR_API_ACTION_PROPERTY_ATTRIBUTE_UPSERT_LIST,
            payload: {
              propertyAttributes: newPropertyAttributesValues,
            },
          }),
        breedRestrictionsRemoved && this._onSaveBreedRestrictions([]),
        dispatch(postWithState, {
          action: SR_API_ACTION_PARTNER_UPDATE_PET_SCREENING,
          payload: {
            partnerId: partner.id,
            petScreeningUrl: formValues.petScreeningUrl,
            petScreeningLoginUserName: formValues.petScreeningLoginUserName,
            petScreeningLoginPassword: formValues.petScreeningLoginPassword,
            petScreeningLoginPasswordConfirm: formValues.petScreeningLoginPasswordConfirm,
          },
        }),
      ]),
    )
      .then(() => {
        return Promise.all([
          dispatch(PartnerActions.loadPartnerBySlug, {
            slug: match.params.partnerSlug,
            forceRefresh: true,
          }),
          dispatch(TenantScreeningCriteriaActions.getTenantScreeningCriteria, {
            partnerSlug: partner.slug,
            reload: true,
          }),
          dispatch(PropertyAttributeActions.getList, {
            partnerId: partner.id,
            category: PROPERTY_ATTRIBUTE_CATEGORY_LISTING_FIELD,
            forceRefresh: true,
          }),
        ]);
      })
      .then(() => {
        this.setState({
          isSubmitting: false,
          editSuccessModalIsOpen: true,
        });
      });
  };

  _onCloseEditSuccessModal = () => {
    this.setState({
      editSuccessModalIsOpen: false,
    });
  };

  _onOpenBreedRestrictionsModal = () => {
    this.setState({
      showBreedRestrictionsModal: true,
    });
  };

  _onCloseBreedRestrictionsModal = () => {
    this.setState({
      showBreedRestrictionsModal: false,
    });
  };

  _onSaveBreedRestrictions = (breedRestrictions) => {
    const { partner, dispatch } = this.props;

    this._onCloseBreedRestrictionsModal();

    return dispatch(postWithState, {
      action: SR_API_ACTION_UPDATE_BREED_RESTRICTIONS,
      payload: {
        partnerId: partner.id,
        breedRestrictions,
      },
      onSuccess: () => {
        dispatch(TenantScreeningCriteriaActions.getTenantScreeningCriteria, {
          partnerSlug: partner.slug,
          reload: true,
        });
      },
    });
  };

  render() {
    const {
      partner,
      currentUser,
      partnerTenantScreeningCriteria,
      breedRestrictionDefinitions,
      propertyAttributes,
      isTransitioning,
      dispatch,
    } = this.props;

    const { isSubmitting, editSuccessModalIsOpen, showBreedRestrictionsModal } = this.state;

    if (!partnerTenantScreeningCriteria || (isTransitioning && !partner)) {
      return null;
    }

    if (!partner) {
      const userLandingPage = getUserLandingPage({
        userRoles: currentUser.roles,
      });
      return <Redirect to={userLandingPage} />;
    }

    const defaultFormValues = getDefaultFormValues({
      partnerTenantScreeningCriteria,
      propertyAttributes,
      partner,
    });
    const breedRestrictions = partnerTenantScreeningCriteria?.breedRestrictions || [];

    return (
      <SrPartnerPageV2
        backUrl={`/partner/${partner.slug}/settings`}
        navbarTitle={partner.displayName}
        pageHeader="Renter &amp; Pet Criteria"
      >
        <Formik
          ref={(formik) => {
            this.formik = formik;
          }}
          initialValues={createInitialValues({
            formFields: getFormFields({ formValues: defaultFormValues }),
            defaultValues: defaultFormValues,
          })}
          validate={(formValues) => {
            const formFields = getFormFields({ formValues });
            return createFormValidationFn(formFields)(formValues);
          }}
          enableReinitialize
          onSubmit={this._onSubmit}
          render={(formikBag) => {
            const formFields = getFormFields({
              formValues: formikBag.values,
              setFieldValue: formikBag.setFieldValue,
            });
            const getFormFieldsById = createFormFieldsByIdGetter(formFields);
            this.dirty = formikBag.dirty;
            const shouldHidePetsField =
              !formikBag.values.catsAllowed && !formikBag.values.dogsAllowed;
            const shouldHideBreedRestrictionsField = !formikBag.values.dogsAllowed;

            return (
              <>
                <FormikContext.Provider value={formikBag}>
                  <Form>
                    <SrCard
                      color={SR_CARD_BULLET_COLOR_PURPLE}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                      header="Renter Qualifications"
                      subheader="Set the criteria you’d like Sunroom to use to process your applications."
                    >
                      <SrGridRow autofit autofitMinColumnWidth="185px">
                        <SrFormikField
                          formField={getFormFieldsById('creditScore')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrFormikField
                          formField={getFormFieldsById('creditScoreQualification')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="185px">
                        <SrFormikField
                          formField={getFormFieldsById('rentToIncomeRatio')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrFormikField
                          formField={getFormFieldsById('moveInDateRangeInDays')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="405px">
                        <SrFormikField
                          formField={getFormFieldsById('feloniesAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth="auto">
                          <SrFormikField
                            formField={getFormFieldsById('feloniesExpirationInYears')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow>
                        <SrText
                          weight="600"
                          font="greycliff"
                          size="16px"
                          color="#132630"
                          paddingTop="18px"
                        >
                          In accordance with Fair Housing guidance, Sunroom does not restrict
                          applicants based on criminal history. Applications will include a
                          background check which can be reviewed in your decision process.
                        </SrText>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="405px">
                        <SrFormikField
                          formField={getFormFieldsById('bankruptciesAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth="auto">
                          <SrFormikField
                            formField={getFormFieldsById('bankruptciesExpirationInYears')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="405px">
                        <SrFormikField
                          formField={getFormFieldsById('evictionsAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth="auto">
                          <SrFormikField
                            formField={getFormFieldsById('evictionsExpirationInYears')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="185px">
                        <SrFormikField
                          formField={getFormFieldsById('section8Accepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="405px">
                        <SrFormikField
                          formField={getFormFieldsById('guarantorAccepted')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrGridRow autofit autofitMinColumnWidth="auto">
                          <SrFormikField
                            formField={getFormFieldsById('guarantorRentToIncomeRatio')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="405px">
                        <SrFormikField
                          formField={getFormFieldsById('verifyRentalHistory')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />

                        <SrGridRow autofit autofitMinColumnWidth="auto">
                          <SrFormikField
                            formField={getFormFieldsById('verifyRentalHistoryOnMinCreditScore')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE_LONG_LABEL}
                          />
                        </SrGridRow>
                      </SrGridRow>
                    </SrCard>

                    <SrCard
                      color={SR_CARD_BULLET_COLOR_PURPLE}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                      header="Pet Policy"
                    >
                      <SrGridRow autofit autofitMinColumnWidth="185px">
                        <SrFormikField
                          formField={getFormFieldsById('catsAllowed')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="185px">
                        <SrFormikField
                          formField={getFormFieldsById('dogsAllowed')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="185px">
                        <SrFormikField
                          formField={getFormFieldsById('maximumPetsAllowed')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                      <SrGridRow autofit autofitMinColumnWidth="185px">
                        <SrFormikField
                          formField={getFormFieldsById('petWeight')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      </SrGridRow>
                    </SrCard>

                    {!shouldHidePetsField && (
                      <SrCard
                        ref={this.petDepositRef}
                        color={SR_CARD_BULLET_COLOR_PURPLE}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                        header="Pet Deposit & Fees"
                      >
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('petDeposit')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrFormikField
                          formField={getFormFieldsById('petDepositPerPet')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('additionalPetDeposit')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('petFee')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrFormikField
                          formField={getFormFieldsById('petFeePerPet')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('additionalPetFee')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('monthlyPetRent')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrFormikField
                          formField={getFormFieldsById('monthlyPetRentPerPet')}
                          formikBag={formikBag}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('additionalMonthlyPetRent')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                      </SrCard>
                    )}

                    {!shouldHideBreedRestrictionsField && (
                      <SrCard color={SR_CARD_BULLET_COLOR_PURPLE} header="Breed Restrictions">
                        <SrBox marginTop="20px">
                          <SrText
                            font="greycliff"
                            fontSize="21px"
                            lineHeight="29px"
                            weight="800"
                            display="block"
                          >
                            Choose dog breeds that are not allowed.
                          </SrText>
                          <SrBox bgColor="yellow-lighter" margin="10px -20px" padding="10px 20px">
                            <SrText font="greycliff" fontSize="16px" weight="800" display="block">
                              {breedRestrictions.length > 0
                                ? `${breedRestrictions.length}
                                ${pluralize('breed', breedRestrictions.length)} restricted`
                                : 'No breeds restricted'}
                            </SrText>
                          </SrBox>
                          <SrGridRow autofit autofitMinColumnWidth="260px">
                            {breedRestrictions.map(({ name, id }) => (
                              <SrBox marginTop="-12px">
                                <SrFormikField
                                  key={id}
                                  formField={{
                                    type: 'multiple_choice',
                                    properties: {
                                      multipleSelection: false,
                                      useArrayValues: false,
                                      choices: [
                                        {
                                          label: name,
                                          value: id,
                                        },
                                      ],
                                      checkboxIconType: 'cancelIconRed',
                                    },
                                  }}
                                  value={id}
                                  theme={SR_FIELD_THEME_ADOBE}
                                />
                              </SrBox>
                            ))}
                          </SrGridRow>
                          <SrBox marginTop="20px" />
                          <SrLabel
                            label="See All Breeds"
                            color="blue300"
                            borderColor="#007AFF"
                            alignCenter
                            justifyCenter
                            onClick={this._onOpenBreedRestrictionsModal}
                          />
                        </SrBox>
                      </SrCard>
                    )}

                    {!shouldHidePetsField && (
                      <SrCard
                        color={SR_CARD_BULLET_COLOR_PURPLE}
                        header="PetScreening.com Integration"
                        subheader={
                          'Please invite Sunroom as a user on PetScreening.com and input the login ' +
                          'info below. Applicants will be sent to your PetScreening.com link.'
                        }
                      >
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('petScreeningUrl')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('petScreeningLoginUserName')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('petScreeningLoginPassword')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                        <SrBox maxWidth="565px">
                          <SrFormikField
                            formField={getFormFieldsById('petScreeningLoginPasswordConfirm')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrBox>
                      </SrCard>
                    )}

                    <SrStaticFooter>
                      <SrText
                        display="block"
                        lineHeight="25px"
                        font="greycliff"
                        color="grey400"
                        fontSize="13px"
                        weight="500"
                      >
                        Updates can take 24 hours to syndicate
                      </SrText>
                      <SrFooterButton
                        onClick={this._onValidateForm}
                        hasBoxShadow={!isSubmitting}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Saving...' : 'Save'}
                      </SrFooterButton>
                    </SrStaticFooter>
                  </Form>
                </FormikContext.Provider>
                <SrRouteLeavingGuard when={formikBag.dirty} dispatch={dispatch} />
              </>
            );
          }}
        />

        {editSuccessModalIsOpen && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmarkSuccessGreen.svg"
            headline={<span>Saved successfully!</span>}
            message="Changes are live instantly on Sunroom."
            buttonText="ok, thanks"
            maxWidth={375}
            onClose={this._onCloseEditSuccessModal}
          />
        )}

        {showBreedRestrictionsModal && (
          <SrBreedRestrictionsModal
            breedRestrictions={breedRestrictions?.map((breed) => breed.name) || []}
            breedRestrictionDefinitions={
              breedRestrictionDefinitions?.map((breed) => breed.name) || []
            }
            onClose={this._onCloseBreedRestrictionsModal}
            onSave={this._onSaveBreedRestrictions}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

PartnerSettingsApplicationsPage.displayName = 'PartnerSettingsApplicationsPage';

export default PartnerSettingsApplicationsPage;
